import React, { useState } from "react";
import { useTermination } from "@/contexts/TerminationContext";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Loader2, Calendar, CheckCircle2, XCircle } from "lucide-react";
import { format } from "date-fns";
import { useToast } from "@/components/ui/use-toast";
import ApiService from "@/services/api";
import { EndpointKeys } from "@/config/api";
import { EmployeeDemographicInfo } from "@/components/types/employeeTypes";
import { UseFormReturn } from "react-hook-form";
import BillingAPIService from "@/services/billing-api";
import type { ProrationMode } from "@/types/billing";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface SummaryStepProps {
  employeeDetails: EmployeeDemographicInfo | null;
  form: UseFormReturn<any>;
}

interface SubmitTerminationResponse {
  success: boolean;
  message?: string;
  terminationId?: string;
}

const SummaryStep: React.FC<SummaryStepProps> = ({ employeeDetails, form }) => {
  const { currentTermination, updateTermination, seatAction, setSeatAction, seatUpdateMode } = useTermination();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  if (!currentTermination || !employeeDetails) {
    return (
      <div className="p-4 bg-yellow-50 rounded-md">
        <p className="text-yellow-700">
          Required information is missing. Please complete all previous steps.
        </p>
      </div>
    );
  }

  const getNoticeTypeLabel = () => {
    const { noticeCalculation } = currentTermination;
    if (!noticeCalculation) return "Not specified";

    if (noticeCalculation.workingNoticePeriod) return "Working Notice Period";
    if (noticeCalculation.paymentInLieuOfNotice)
      return "Payment in Lieu of Notice (PILON)";

    return "Not specified";
  };

  const calculateTotalPayment = () => {
    if (
      !currentTermination.final_pay_components ||
      currentTermination.final_pay_components.length === 0
    ) {
      return 0;
    }

    return currentTermination.final_pay_components.reduce(
      (total, component) => {
        return component.type === "payment"
          ? total + component.amount
          : total - component.amount;
      },
      0,
    );
  };

  const handleSubmitTermination = async () => {
    try {
      setIsSubmitting(true);

      // Skip updating the termination record first since submittedAt is not part of UpdateTerminationRequest
      // We'll let the submission API handle setting the submission timestamp

      // Make API call to submit termination
      if (!currentTermination.id) {
        return;
      }
      const response = await ApiService.request<SubmitTerminationResponse>(
        EndpointKeys.SUBMIT_TERMINATION,
        {
          method: "POST",
          urlParams: { termination_id: currentTermination.id },
          body: {
            ...currentTermination,
            employeeId: employeeDetails.id,
          },
        },
      );

      if (response.success) {
        // If user selected reduce seats, request seat quantity decrease at cycle end
        if (seatAction === "reduce") {
          try {
            const summary = await BillingAPIService.getSeatSummary();
            const newQuantity = Math.max(0, summary.paid_seats - 1);
            await BillingAPIService.updateSeats(
              undefined,
              newQuantity,
              (seatUpdateMode as ProrationMode) || "end_of_cycle",
            );
          } catch (e) {
            console.error("Failed to request seat reduction after termination", e);
          }
        }
        toast({
          title: "Termination submitted successfully",
          description: "The termination process has been initiated.",
          variant: "default",
        });

        // Navigate to employee page or termination confirmation
        navigate(`/employee/${employeeDetails.id}`);
      } else {
        toast({
          title: "Submission failed",
          description:
            response.message ||
            "An error occurred while submitting the termination.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error submitting termination:", error);
      toast({
        title: "Submission failed",
        description:
          "An unexpected error occurred while submitting the termination.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getPayTypeLabel = (payType?: string) => {
    if (!payType) return "Not specified";
    
    switch (payType) {
      case "HOLIDAY_PAY":
        return "Holiday Pay";
      case "SALARY":
        return "Salary";
      case "PILON":
        return "Pay in lieu of Notice";
      case "REDUNDANCY_PAY":
        return "Redundancy Pay";
      case "OTHER":
        return "Other";
      default:
        return payType; // Return the original value if not matched
    }
  };

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <p>
          Review all the termination details below before submission. Once
          submitted, the termination process will be initiated and relevant
          notifications will be sent.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Employee Information</CardTitle>
          <CardDescription>
            Basic details of the employee being terminated
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableBody>
              <TableRow>
                <TableHead className="w-[200px]">Employee Name</TableHead>
                <TableCell>
                  {employeeDetails.firstName} {employeeDetails.lastName}
                </TableCell>
              </TableRow>
              {employeeDetails.actualEffectiveDate && (
                <TableRow>
                  <TableHead>Employment Start Date</TableHead>
                  <TableCell>
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 mr-2 text-gray-500" />
                      {format(
                        new Date(employeeDetails.actualEffectiveDate),
                        "PPP",
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Termination Details</CardTitle>
          <CardDescription>
            Key information about the termination
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableBody>
              <TableRow>
                <TableHead className="w-[200px]">Reason</TableHead>
                <TableCell className="capitalize">
                  {currentTermination.termination_reason || "Not specified"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableHead>Termination Date</TableHead>
                <TableCell>
                  <div className="flex items-center">
                    <Calendar className="h-4 w-4 mr-2 text-gray-500" />
                    {format(
                      new Date(currentTermination.termination_date),
                      "PPP",
                    )}
                  </div>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableHead>Notice Type</TableHead>
                <TableCell>{getNoticeTypeLabel()}</TableCell>
              </TableRow>
              {currentTermination.noticeCalculation?.payThroughDate && (
                <TableRow>
                  <TableHead>Pay Through Date</TableHead>
                  <TableCell>
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 mr-2 text-gray-500" />
                      {format(
                        new Date(
                          currentTermination.noticeCalculation.payThroughDate,
                        ),
                        "PPP",
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              )}
              <TableRow>
                <TableHead>Total Final Payment</TableHead>
                <TableCell className="font-medium">
                  £{calculateTotalPayment().toFixed(2)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
          <div className="mt-6">
            <div className="mb-2 font-medium">Seat handling after termination</div>
            <div className="flex items-center gap-2">
              <Select value={seatAction} onValueChange={(v) => setSeatAction(v as "keep" | "reduce") }>
                <SelectTrigger className="w-[260px]">
                  <SelectValue placeholder="Choose seat action" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="keep">Keep seat (default)</SelectItem>
                  <SelectItem value="reduce">Reduce seats at end of cycle</SelectItem>
                </SelectContent>
              </Select>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="text-sm text-muted-foreground cursor-help">What does this mean?</span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Reducing seats lowers your subscription quantity by one at the end of the current billing cycle.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Final Pay Components</CardTitle>
          <CardDescription>
            Breakdown of the final payment calculation
          </CardDescription>
        </CardHeader>
        <CardContent>
          {currentTermination.final_pay_components &&
          currentTermination.final_pay_components.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Description</TableHead>
                  <TableHead>Pay Type</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Amount (£)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {currentTermination.final_pay_components.map((component) => (
                  <TableRow key={component.id}>
                    <TableCell>{component.description}</TableCell>
                    <TableCell>
                      {getPayTypeLabel(component.pay_type || component.type_code)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center">
                        {component.type === "payment" ? (
                          <CheckCircle2 className="h-4 w-4 mr-2 text-green-500" />
                        ) : (
                          <XCircle className="h-4 w-4 mr-2 text-red-500" />
                        )}
                        {component.type === "payment" ? "Payment" : "Deduction"}
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {component.type === "deduction" && "-"}£
                      {component.amount.toFixed(2)}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={3} className="font-bold text-right">
                    Total
                  </TableCell>
                  <TableCell className="text-right font-bold">
                    £{calculateTotalPayment().toFixed(2)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          ) : (
            <div className="p-4 text-center text-gray-500">
              No final pay components have been added.
            </div>
          )}
        </CardContent>
        <CardFooter className="bg-gray-50 p-4 flex flex-col items-start">
          <p className="text-sm text-gray-600 mb-2">
            <strong>Note:</strong> This calculation is an estimate. The actual
            final payment may vary based on payroll processing and tax
            calculations.
          </p>
          <p className="text-sm text-gray-600">
            Once submitted, the termination will be processed according to UK
            employment regulations.
          </p>
        </CardFooter>
      </Card>

      <div className="flex justify-center mt-8">
        <Button
          onClick={handleSubmitTermination}
          disabled={isSubmitting}
          className="w-full max-w-md py-6 text-lg bg-green-600 hover:bg-green-700"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Processing...
            </>
          ) : (
            "Submit Termination Request"
          )}
        </Button>
      </div>

      <div className="p-4 bg-yellow-50 rounded-md">
        <h3 className="font-medium text-yellow-800">Important Note</h3>
        <p className="text-sm text-yellow-700 mt-2">
          By submitting this termination request, you confirm that all details
          are correct and that the termination process complies with UK
          employment law. This action will initiate the formal termination
          process and cannot be easily reversed.
        </p>
      </div>
    </div>
  );
};

export default SummaryStep;
